import { isValueSet } from "common/utils";
import { PriceSummaryItemKind, PriceSummary } from "common/types/priceSummary";
import { getSummaryItems } from "common/utils/summary";
import { PaymentMethodChargeabilityStatus, WalletType } from "common/types";
import { Optional } from "common/utils/types";

import { TextChipProps } from "client/common/components/content/TextChip";

type SummaryItemWithAmount = {
  kind: PriceSummaryItemKind;
  label: string;
  amount: number;
  value: string;
};

export const getWalletPaymentItems = <T extends PriceSummary>(data: T) =>
  // total will be rendered separately so we remove it here to avoid repeating it
  getSummaryItems<T>(data)
    .filter(
      (pi): pi is SummaryItemWithAmount =>
        isValueSet(pi.amount) && pi.kind !== PriceSummaryItemKind.Total,
    )
    .map((pi) => ({ label: pi.label, amount: pi.amount }));

/**
 * We want to support one checkout button for either apple pay or google pay for now
 * Let's filter out the supported wallets just in case other's appear which should also
 * match the general logic we get from stripe elements for the WalletPaymentButton.
 */
export const resolveSupportedWalletByPriority = (
  supportedWallets: Set<WalletType>,
): WalletType | undefined => {
  const walletPriorityOrder = [WalletType.APPLE, WalletType.GOOGLE];

  return walletPriorityOrder.find((wallet) => supportedWallets.has(wallet));
};

type WalletMetadata = {
  label: string;
};

const walletToMetadata: Record<WalletType, WalletMetadata> = {
  [WalletType.APPLE]: { label: "Apple Pay" },
  [WalletType.GOOGLE]: { label: "Google Pay" },
  [WalletType.SAMSUNG]: { label: "Samsung Pay" },
  [WalletType.MASTERPASS]: { label: "Masterpass" },
  [WalletType.AMEX_EXPRESS]: { label: "Amex Express" },
  [WalletType.VISA_CHECKOUT]: { label: "Visa Checkout" },
};

export const walletDisplayText = (supportedWallets: WalletType): string => {
  return walletToMetadata[supportedWallets].label;
};

type PaymentMethodChargeabilityChip = { label: string; variant: TextChipProps["variant"] };

type PaymentMethodChargeabilityMeta = {
  isChargeable: boolean;
  chip: PaymentMethodChargeabilityChip | null;
};

const paymentMethodChargeabilityStatusToMeta: Record<
  PaymentMethodChargeabilityStatus,
  PaymentMethodChargeabilityMeta
> = {
  [PaymentMethodChargeabilityStatus.Valid]: { isChargeable: true, chip: null },
  [PaymentMethodChargeabilityStatus.Expired]: {
    isChargeable: false,
    chip: { label: "Expired", variant: "danger" },
  },
  [PaymentMethodChargeabilityStatus.Deleted]: {
    isChargeable: false,
    chip: { label: "Removed", variant: "danger" },
  },
};

/**
 * Minimal payment-option shape the chargeability helpers read: an id (which may be a
 * wallet/Klarna placeholder sentinel) and an optional chargeability status. Any
 * PaymentMethodOption satisfies this, as does a subscription's { id, chargeability_status }.
 */
export type ChargeabilityPaymentOption = {
  id: number | "__wallet__" | "__klarna__";
  chargeability_status?: PaymentMethodChargeabilityStatus;
};

export const getPaymentOptionChargeabilityStatus = (
  option: ChargeabilityPaymentOption,
): Optional<PaymentMethodChargeabilityStatus> => {
  if (option.id === "__wallet__" || option.id === "__klarna__") {
    return undefined;
  }
  return option.chargeability_status;
};

const getPaymentOptionChargeabilityMeta = (
  option: ChargeabilityPaymentOption,
): PaymentMethodChargeabilityMeta => {
  // Fail-open default: wallet / Klarna placeholders (no status) and any unmapped status
  // (BE/FE enum drift) stay selectable rather than crashing the row.
  const defaultPaymentMethodMeta: PaymentMethodChargeabilityMeta = {
    isChargeable: true,
    chip: null,
  };
  const status = getPaymentOptionChargeabilityStatus(option);
  if (!status) {
    return defaultPaymentMethodMeta;
  }
  return paymentMethodChargeabilityStatusToMeta[status] ?? defaultPaymentMethodMeta;
};

export const isPaymentOptionChargeable = (option: ChargeabilityPaymentOption): boolean =>
  getPaymentOptionChargeabilityMeta(option).isChargeable;

export const getPaymentOptionChargeabilityChip = (
  option: ChargeabilityPaymentOption,
): PaymentMethodChargeabilityChip | undefined =>
  getPaymentOptionChargeabilityMeta(option).chip ?? undefined;

export const UNCHARGEABLE_PAYMENT_METHOD_MESSAGE =
  "Please select a valid payment method to continue.";
